function canTrackAnalytics() {
  if (window.forceConsentForTesting) return true;

  return window.Shopify?.customerPrivacy?.currentVisitorConsent?.().analytics === 'yes';
}

function pushToDataLayer(eventName, ecommerce, extra) {
  if (!canTrackAnalytics()) return;

  // currency lives in analyticsContext as the source of truth, but must only reach the
  // dataLayer inside an event's ecommerce object, not spread onto every event by default.
  const { currency, ...context } = window.analyticsContext ?? {};

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...context,
    ...(ecommerce !== undefined && { ecommerce }),
    ...extra,
  });
}

// window.analyticsContext.cart_id is rendered once at page load and stays null until a
// cart exists, so add_to_cart reads the live value from the cart cookie Shopify sets instead.
function getCartId() {
  return document.cookie.match(/(?:^|; )cart=([^;]*)/)?.[1];
}

// Reads the GA4 list attribution declared on the nearest [data-product-list] container,
// so list-scoped events can be attributed to the list the interaction came from.
// Lists that declare no id report nothing extra.
function getListContext(element) {
  const { listId, listName } = element?.closest('[data-product-list]')?.dataset ?? {};
  return listId && { item_list_id: listId, item_list_name: listName };
}

class ProductListObserver {
  constructor(container) {
    this.container = container;
    this.listContext = getListContext(container);
    this.seenItemIds = new Set();
    this.pendingItems = [];
    this.flushTimer = null;
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), { threshold: 0.5 });
    this.handleClick = this.handleClick.bind(this);
    this.flushPendingItems = this.flushPendingItems.bind(this);
    activeProductListObservers.add(this);
    // select_item stays on 'click' (not pointerdown/mousedown): click is the one event the
    // browser guarantees never fires after a scroll or drag, on any device, so it can't produce
    // false positives. Navigation is held back by a flat SELECT_ITEM_NAV_DELAY_MS budget instead
    // of GTM's eventCallback, which only confirms the tag was *dispatched*, not that the network
    // request actually reached the wire before the previous attempt's navigation aborted it.
    this.container.addEventListener('click', this.handleClick);
    this.observeItems();
  }

  observeItems() {
    this.container.querySelectorAll('[data-analytics-item]:not([data-analytics-observed])').forEach((item) => {
      item.setAttribute('data-analytics-observed', 'true');
      this.observer.observe(item);
    });
  }

  handleIntersection(entries) {
    const newlyVisibleItems = entries
      .filter((entry) => entry.isIntersecting && !this.seenItemIds.has(entry.target.dataset.itemId))
      .map((entry) => {
        this.seenItemIds.add(entry.target.dataset.itemId);
        return this.buildItem(entry.target);
      });

    if (newlyVisibleItems.length === 0) return;

    this.pendingItems.push(...newlyVisibleItems);
    clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(this.flushPendingItems, VIEW_ITEM_LIST_BATCH_DELAY_MS);
  }

  flushPendingItems() {
    clearTimeout(this.flushTimer);
    this.flushTimer = null;
    if (this.pendingItems.length === 0) return;

    const items = this.pendingItems;
    this.pendingItems = [];

    pushToDataLayer(ANALYTICS_EVENTS.viewItemList, {
      currency: window.analyticsContext?.currency,
      ...this.listContext,
      items,
    });
  }

  buildItem(item) {
    return {
      item_id: item.dataset.itemId,
      price: Number(item.dataset.price),
      discount: Number(item.dataset.discount || 0),
      item_name: item.dataset.itemName,
      index: Number(item.dataset.index) || 0,
      currency: window.analyticsContext?.currency,
    };
  }

  handleClick(event) {
    if (!this.trackSelectItem(event)) return;
    this.delayNavigationIfNeeded(event);
  }

  trackSelectItem(event) {
    const item = event.target.closest('[data-analytics-item]');
    if (!item) return null;

    // Add-to-cart controls (quick add / bulk add buttons) live inside the same
    // [data-analytics-item] card but aren't a product selection, so they must not fire select_item.
    if (event.target.closest('.quick-add__submit')) return null;

    // The clicked card's view_item_list may still be batched; send it first to keep the view -> select order.
    this.flushPendingItems();

    pushToDataLayer(ANALYTICS_EVENTS.selectItem, {
      currency: window.analyticsContext?.currency,
      ...this.listContext,
      items: [this.buildItem(item)],
    });

    return item;
  }

  // Holds back navigation for a flat SELECT_ITEM_NAV_DELAY_MS so the tracking request triggered
  // above has time to actually leave the client, rather than trusting any GTM-side signal (see
  // constructor comment). Skipped for links opening in a new tab or modified clicks (ctrl/cmd/
  // shift/middle), since those don't unload the current page.
  delayNavigationIfNeeded(event) {
    const link = event.target.closest('a[href]');
    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1;
    if (!link || link.target === '_blank' || isModifiedClick || !canTrackAnalytics()) return;

    event.preventDefault();
    setTimeout(() => {
      window.location.href = link.href;
    }, SELECT_ITEM_NAV_DELAY_MS);
  }

  disconnect() {
    this.flushPendingItems();
    this.observer.disconnect();
    this.container.removeEventListener('click', this.handleClick);
    activeProductListObservers.delete(this);
  }
}

const productListObservers = new WeakMap();

// Strong references to every live observer, so the shared page-level listeners below can flush them.
// Containers replaced via innerHTML or removed from the page are never explicitly disconnected, so
// detached ones are pruned whenever nodes leave the DOM, otherwise this set would keep them (and
// their observers) alive forever.
const activeProductListObservers = new Set();

function pruneDetachedProductListObservers() {
  activeProductListObservers.forEach((observer) => {
    if (observer.container.isConnected) return;
    observer.disconnect();
    productListObservers.delete(observer.container);
  });
}

function flushAllProductListObservers() {
  activeProductListObservers.forEach((observer) => observer.flushPendingItems());
}

// One shared pair of listeners for all lists: pending items must not be lost if the user leaves
// or hides the page before the batch delay elapses.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushAllProductListObservers();
});
window.addEventListener('pagehide', flushAllProductListObservers);

new MutationObserver((mutations) => {
  if (activeProductListObservers.size === 0) return;
  if (mutations.some((mutation) => mutation.removedNodes.length > 0)) pruneDetachedProductListObservers();
}).observe(document.documentElement, { childList: true, subtree: true });

function initProductListTracking(root = document) {
  const containers = new Set(root.querySelectorAll('[data-product-list]'));
  if (root.matches?.('[data-product-list]')) containers.add(root);

  containers.forEach((container) => {
    const existingObserver = productListObservers.get(container);
    if (existingObserver) {
      existingObserver.observeItems();
    } else {
      productListObservers.set(container, new ProductListObserver(container));
    }
  });
}

function resetProductListTracking(container) {
  if (!container) return;
  productListObservers.get(container)?.disconnect();
  productListObservers.set(container, new ProductListObserver(container));
}

let pageViewTracked = false;

function trackPageView() {
  if (pageViewTracked) return;
  pageViewTracked = true;
  pushToDataLayer(ANALYTICS_EVENTS.pageView);
}

function trackNewsletterSubscription(subscriptionMethod) {
  pushToDataLayer(ANALYTICS_EVENTS.newsletterSubscription, undefined, { subscription_method: subscriptionMethod });
}

let newsletterSubscriptionTracked = false;

function trackNewsletterSubscriptionIfPending() {
  if (newsletterSubscriptionTracked) return;
  const signal = document.querySelector('[data-newsletter-subscription-signal]');
  if (!signal) return;

  newsletterSubscriptionTracked = true;
  const { subscription_method } = JSON.parse(signal.textContent);
  trackNewsletterSubscription(subscription_method);

  const url = new URL(window.location.href);
  url.searchParams.delete('customer_posted');
  history.replaceState(null, '', url);
}

function trackViewItem(item) {
  pushToDataLayer(ANALYTICS_EVENTS.viewItem, {
    currency: window.analyticsContext?.currency,
    items: [{ ...item, index: Number(item.index) || 0, currency: window.analyticsContext?.currency }],
  });
}

let viewItemTracked = false;

function trackViewItemIfPending() {
  if (viewItemTracked) return;
  const signal = document.querySelector('[data-view-item-signal]');
  if (!signal) return;

  viewItemTracked = true;
  trackViewItem(JSON.parse(signal.textContent));
}

function trackViewCart(cart) {
  pushToDataLayer(ANALYTICS_EVENTS.viewCart, {
    currency: cart.currency,
    value: cart.value,
    items: cart.items.map((item) => ({ ...item, index: Number(item.index) || 0, currency: cart.currency })),
  });
}

let viewCartTracked = false;

function trackViewCartIfPending() {
  if (viewCartTracked) return;
  const signal = document.querySelector('[data-view-cart-signal]');
  if (!signal) return;

  viewCartTracked = true;
  trackViewCart(JSON.parse(signal.textContent));
}

function trackAddToCart(item, sourceElement) {
  const discount = (item.price - item.final_price) / 100;
  const listContext = getListContext(sourceElement);
  const index = Number(sourceElement?.closest('[data-analytics-item]')?.dataset.index) || 0;

  pushToDataLayer(ANALYTICS_EVENTS.addToCart, {
    currency: window.analyticsContext?.currency,
    cart_id: getCartId(),
    value: item.final_line_price / 100,
    items: [
      {
        item_id: item.sku || item.product_id,
        price: item.final_price / 100,
        discount,
        item_name: item.product_title,
        quantity: item.quantity,
        index,
        currency: window.analyticsContext?.currency,
        ...listContext,
      },
    ],
  });
}

function trackAddToCartFromCartState(cart, addedVariantIds) {
  const addedItems = cart.items.filter((item) => addedVariantIds.includes(item.variant_id ?? item.id));
  if (!addedItems.length) return;

  pushToDataLayer(ANALYTICS_EVENTS.addToCart, {
    currency: window.analyticsContext?.currency,
    cart_id: getCartId(),
    value: addedItems.reduce((sum, item) => sum + item.final_line_price / 100, 0),
    items: addedItems.map((item, index) => ({
      item_id: item.sku || item.product_id,
      price: item.final_price / 100,
      discount: (item.original_line_price - item.final_line_price) / 100,
      item_name: item.product_title,
      quantity: item.quantity,
      index,
      currency: window.analyticsContext?.currency,
    })),
  });
}

let addToCartTrackingInitialized = false;

function initAddToCartTracking() {
  if (addToCartTrackingInitialized) return;
  addToCartTrackingInitialized = true;

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === 'product-form') {
      trackAddToCart(event.cartData, event.sourceElement);
      return;
    }

    if (event.source === 'routine-section-bundle') {
      const addedVariantIds = (event.cartData?.items || []).map((item) => item.variant_id ?? item.id).filter(Boolean);
      trackAddToCartFromCartState(event.cartData, addedVariantIds);
      return;
    }

    if (event.source === 'quick-add') {
      trackAddToCartFromCartState(event.cartData, event.updatedVariantIds || []);
      return;
    }

    if (event.source === 'cart-items') {
      // The cart page's own quantity stepper/input reuses this same event for increases,
      // decreases and removals, so only increases with a known variant must report add_to_cart.
      if (event.isIncrease && event.variantId) {
        trackAddToCartFromCartState(event.cartData, [parseInt(event.variantId)]);
      }
    }
  });
}

function startTrackingIfConsented() {
  if (!canTrackAnalytics()) return;
  trackPageView();
  trackViewItemIfPending();
  trackViewCartIfPending();
  trackNewsletterSubscriptionIfPending();
  initProductListTracking();
  initAddToCartTracking();
}

document.addEventListener('DOMContentLoaded', startTrackingIfConsented);
document.addEventListener('visitorConsentCollected', startTrackingIfConsented);

window.initProductListTracking = initProductListTracking;
window.resetProductListTracking = resetProductListTracking;
